import { useCallback, useRef, useState } from 'react';
import { useMedplum } from '@medplum/react';
import { Bundle, ServiceRequest, Specimen } from '@medplum/fhirtypes';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { createReference, resolveId } from '@medplum/core';

interface UseLinkSpecimensProps {
  serviceRequest: ServiceRequest | undefined;
  requiredTypeCode: string | undefined;
  onSuccess: () => Promise<void>;
  onClose: () => void;
}

export function useLinkSpecimens({
  serviceRequest,
  requiredTypeCode,
  onSuccess,
  onClose,
}: UseLinkSpecimensProps) {
  const medplum = useMedplum();
  const { t } = useTranslation(['lims', 'common']);
  const [linkingLoading, setLinkingLoading] = useState(false);
  const isLinkingRef = useRef(false);

  const linkSpecimens = useCallback(
    async (selectedSpecimens: Specimen[]) => {
      if (!serviceRequest) {
        return;
      }

      // Prevent concurrent submissions (e.g., double-clicks)
      if (isLinkingRef.current || linkingLoading) {
        return;
      }

      // Validate sample type matches order requirement (ValueSet code)
      const mismatched = selectedSpecimens.filter((s) => {
        const specimenType = s.type?.coding?.[0]?.code;
        return requiredTypeCode && specimenType !== requiredTypeCode;
      });
      if (mismatched.length > 0) {
        notifications.show({
          title: t('error', { ns: 'common' }),
          message: t('orderDetails.validation.typeMismatch', { ns: 'lims' }),
          color: 'red',
        });
        return;
      }

      setLinkingLoading(true);
      isLinkingRef.current = true;

      try {
        const serviceRequestRef = createReference(serviceRequest);

        // Get currently linked specimen IDs from ServiceRequest
        const currentLinkedIds = new Set(
          serviceRequest?.specimen?.map((ref) => resolveId(ref)).filter(Boolean)
        );

        // Get selected specimen IDs
        const selectedIds = new Set(
          selectedSpecimens.map((s) => s.id).filter(Boolean)
        );

        // Specimens to unlink: currently linked but not in the new selection
        const idsToUnlink = Array.from(currentLinkedIds).filter(
          (id) => !selectedIds.has(id)
        );

        // Specimens to link: in selection but not currently linked
        const specimensToLink = selectedSpecimens.filter(
          (s) => s.id && !currentLinkedIds.has(s.id)
        );

        // Read the full Specimen resources we need to unlink so we can build
        // the updated versions (removing the SR ref from Specimen.request).
        const specimensToUnlink: Specimen[] = await Promise.all(
          idsToUnlink
            .filter((id): id is string => Boolean(id))
            .map((id) => medplum.readResource('Specimen', id))
        );

        // Build the transaction bundle entries — everything or nothing.
        const bundleEntries: Bundle['entry'] = [];

        // Entries: unlink — remove SR ref from Specimen.request
        for (const specimen of specimensToUnlink) {
          const updatedRequests = (specimen.request || []).filter(
            (ref) => ref.reference !== serviceRequestRef.reference
          );
          bundleEntries.push({
            resource: { ...specimen, request: updatedRequests },
            request: { method: 'PUT', url: `Specimen/${specimen.id}` },
          });
        }

        // Entries: link — add SR ref to Specimen.request
        for (const specimen of specimensToLink) {
          bundleEntries.push({
            resource: {
              ...specimen,
              request: [...(specimen.request || []), serviceRequestRef],
            },
            request: { method: 'PUT', url: `Specimen/${specimen.id}` },
          });
        }

        // Entry: update ServiceRequest.specimen array
        const specimenReferences = selectedSpecimens.map((specimen) => ({
          reference: `Specimen/${specimen.id}`,
          display: specimen.identifier?.[0]?.value || specimen.id,
        }));

        const updatedServiceRequest: ServiceRequest = {
          ...serviceRequest,
          specimen: specimenReferences,
        };

        bundleEntries.push({
          resource: updatedServiceRequest,
          request: {
            method: 'PUT',
            url: `ServiceRequest/${serviceRequest.id}`,
          },
        });

        const bundle: Bundle = {
          resourceType: 'Bundle',
          type: 'transaction',
          entry: bundleEntries,
        };

        // Execute atomically — if any entry fails the whole transaction rolls back
        await medplum.executeBatch(bundle);

        await onSuccess();

        notifications.show({
          title: t('success', { ns: 'common' }),
          message: t('orderDetails.specimensLinked', { ns: 'lims' }),
          color: 'green',
        });

        onClose();
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : t('orderDetails.specimensLinkError', { ns: 'lims' });
        notifications.show({
          title: t('error', { ns: 'common' }),
          message: errorMessage,
          color: 'red',
        });
      } finally {
        setLinkingLoading(false);
        isLinkingRef.current = false;
      }
    },
    [
      serviceRequest,
      requiredTypeCode,
      linkingLoading,
      medplum,
      t,
      onSuccess,
      onClose,
    ]
  );

  return {
    linkSpecimens,
    linkingLoading,
  };
}
